// Block / 挿入コマンドの単一定義。slash menu と toolbar dropdown で共通利用。

import { $getSelection, $isRangeSelection, $createParagraphNode } from "lexical"
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text"
import { INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from "@lexical/list"
import { $createCodeNode } from "@lexical/code"
import { INSERT_TABLE_COMMAND } from "@lexical/table"
import { $setBlocksType } from "@lexical/selection"
import { $insertNodeToNearestRoot } from "@lexical/utils"

import { $createCalloutNode } from "./callout_node"
import { $createMacroNode, normalizeEmbedSrc } from "./macro_node"
import { pickImageFile }      from "./image_upload"

const setBlock = (editor, creator) => editor.update(() => {
  const sel = $getSelection()
  if ($isRangeSelection(sel)) $setBlocksType(sel, creator)
})

// グループ別の command 定義
export const BLOCK_TYPE_GROUPS = [
  {
    label: "テキスト",
    items: [
      { id: "paragraph", title: "段落",   desc: "通常テキスト", icon: "¶",
        aliases: ["paragraph", "段落", "p", "text"],
        run: (editor) => setBlock(editor, () => $createParagraphNode()) },
      { id: "h1", title: "見出し 1", desc: "ページ大タイトル", icon: "H₁",
        aliases: ["h1", "heading1", "見出し1", "title"],
        run: (editor) => setBlock(editor, () => $createHeadingNode("h1")) },
      { id: "h2", title: "見出し 2", desc: "セクション見出し", icon: "H₂",
        aliases: ["h2", "heading2", "見出し2"],
        run: (editor) => setBlock(editor, () => $createHeadingNode("h2")) },
      { id: "h3", title: "見出し 3", desc: "サブセクション", icon: "H₃",
        aliases: ["h3", "heading3", "見出し3"],
        run: (editor) => setBlock(editor, () => $createHeadingNode("h3")) },
      { id: "h4", title: "見出し 4", desc: "段落見出し", icon: "H₄",
        aliases: ["h4", "heading4", "見出し4"],
        run: (editor) => setBlock(editor, () => $createHeadingNode("h4")) },
    ],
  },
  {
    label: "リスト・引用",
    items: [
      { id: "ul", title: "箇条書き",     desc: "• 順序のないリスト", icon: "≡",
        aliases: ["ul", "bullet", "リスト"],
        run: (editor) => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined) },
      { id: "ol", title: "番号付きリスト", desc: "1. 順序付きリスト", icon: "1.",
        aliases: ["ol", "ordered", "番号"],
        run: (editor) => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined) },
      { id: "quote", title: "引用", desc: "❝ 引用ブロック", icon: "❝",
        aliases: ["quote", "blockquote", "引用"],
        run: (editor) => setBlock(editor, () => $createQuoteNode()) },
      { id: "code", title: "コードブロック", desc: "シンタックスハイライト", icon: "{}",
        aliases: ["code", "コード"],
        run: (editor) => setBlock(editor, () => $createCodeNode()) },
    ],
  },
]

export const INSERT_GROUPS = [
  {
    label: "メディア",
    items: [
      { id: "image", title: "画像", desc: "🖼 ファイル選択でアップロード", icon: "🖼",
        aliases: ["image", "img", "画像", "写真"],
        run: (editor) => {
          if (typeof editor.akebonoUploadImage !== "function") {
            window.alert("画像挿入はページ編集画面でのみ利用できます")
            return
          }
          pickImageFile(file => editor.akebonoUploadImage(file))
        } },
      { id: "drawio", title: "DrawIO 図", desc: "📊 ダイアグラムを作成・編集", icon: "📊",
        aliases: ["drawio", "diagram", "図", "ダイアグラム"],
        run: (editor) => {
          if (typeof editor.akebonoInsertDrawio !== "function") {
            window.alert("図の挿入はページ編集画面でのみ利用できます")
            return
          }
          editor.akebonoInsertDrawio()
        } },
      { id: "anchor", title: "アンカー", desc: "⚓ 名前付きリンク先 (#name)", icon: "⚓",
        aliases: ["anchor", "アンカー", "リンク先"],
        run: (editor) => {
          if (typeof editor.akebonoInsertAnchor !== "function") {
            window.alert("アンカーの挿入はページ編集画面でのみ利用できます")
            return
          }
          editor.akebonoInsertAnchor()
        } },
    ],
  },
  {
    label: "基本",
    items: [
      { id: "table", title: "テーブル", desc: "3 × 3 の表", icon: "▦",
        aliases: ["table", "表"],
        run: (editor) => editor.dispatchCommand(INSERT_TABLE_COMMAND, { rows: "3", columns: "3" }) },
      { id: "hr", title: "区切り線", desc: "水平線", icon: "—",
        aliases: ["hr", "divider", "区切り"],
        run: (editor) => editor.update(() => {
          const sel = $getSelection()
          if ($isRangeSelection(sel)) {
            const p = $createParagraphNode()
            p.append()
            $insertNodeToNearestRoot(p)
          }
        }) },
    ],
  },
  {
    label: "Callout (色付きパネル)",
    items: [
      { id: "callout-info", title: "情報",  desc: "ℹ︎ 青色の情報パネル", icon: "ℹ︎",
        aliases: ["info", "callout", "情報"],
        run: (editor) => insertCallout(editor, "info") },
      { id: "callout-warn", title: "警告",  desc: "⚠ 黄色の警告パネル", icon: "⚠",
        aliases: ["warn", "警告"],
        run: (editor) => insertCallout(editor, "warn") },
      { id: "callout-success", title: "成功", desc: "✓ 緑色のサクセスパネル", icon: "✓",
        aliases: ["success", "成功"],
        run: (editor) => insertCallout(editor, "success") },
      { id: "callout-danger", title: "危険",  desc: "✗ 赤色の警告パネル", icon: "✗",
        aliases: ["danger", "危険"],
        run: (editor) => insertCallout(editor, "danger") },
    ],
  },
  {
    label: "動的マクロ (表示時にサーバーが生成)",
    items: [
      { id: "macro-toc", title: "目次", desc: "TOC を自動生成", icon: "☰",
        aliases: ["toc", "目次"],
        run: (editor) => editor.update(() => $insertNodeToNearestRoot($createMacroNode("toc"))) },
      { id: "macro-children", title: "子ページ一覧", desc: "このページの子ページ一覧", icon: "📁",
        aliases: ["children", "子ページ"],
        run: (editor) => editor.update(() => $insertNodeToNearestRoot($createMacroNode("children"))) },
      { id: "macro-embed", title: "埋め込み", desc: "Google Slides / YouTube 等を埋め込む", icon: "🔗",
        aliases: ["embed", "iframe", "slide", "slides", "youtube", "vimeo", "figma", "埋め込み"],
        run: (editor) => insertEmbed(editor) },
    ],
  },
]

const EMBED_HINT = "Google Slides / Sheets / YouTube / Vimeo / Figma などの https URL"

function insertEmbed(editor) {
  const input = window.prompt(`埋め込み URL\n(${EMBED_HINT})`, "")
  if (input === null) return
  const src = normalizeEmbedSrc(input)
  if (!src) {
    window.alert(`対応していない URL です。\n${EMBED_HINT} を指定してください。`)
    return
  }
  editor.update(() => $insertNodeToNearestRoot($createMacroNode("embed", src)))
}

function insertCallout(editor, variant) {
  editor.update(() => {
    const callout = $createCalloutNode(variant)
    callout.append($createParagraphNode())
    $insertNodeToNearestRoot(callout)
  })
}

// slash menu 用フラット配列 (全コマンド)
export const ALL_COMMANDS = [
  ...BLOCK_TYPE_GROUPS.flatMap(g => g.items),
  ...INSERT_GROUPS.flatMap(g => g.items),
]
